import Phaser from "phaser";

/**
 * Full-cycle duration in ms.  One complete day/night loop.
 * 120 s default — fast enough to notice, slow enough to feel atmospheric.
 */
const CYCLE_DURATION_MS = 120_000;

interface Phase {
  /** Fraction of the cycle [0–1] where this phase starts. */
  at: number;
  tint: number;
  alpha: number;
}

/**
 * Keyframes around the cycle.  Interpolated linearly between neighbours.
 *
 *   0.00  dawn        warm gold, faint
 *   0.20  midday      clear, nearly invisible
 *   0.45  sunset      amber
 *   0.60  dusk        deep blue
 *   0.80  midnight    dark blue
 *   1.00  wraps → dawn
 */
const PHASES: Phase[] = [
  { at: 0.0, tint: 0xffc87c, alpha: 0.08 },
  { at: 0.2, tint: 0xfff8e0, alpha: 0.02 },
  { at: 0.45, tint: 0xff9944, alpha: 0.12 },
  { at: 0.6, tint: 0x3344aa, alpha: 0.25 },
  { at: 0.8, tint: 0x1a1a44, alpha: 0.35 },
  { at: 1.0, tint: 0xffc87c, alpha: 0.08 },
];

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff,
    ag = (a >> 8) & 0xff,
    ab = a & 0xff;
  const br = (b >> 16) & 0xff,
    bg = (b >> 8) & 0xff,
    bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const blue = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | blue;
}

export class DayNightCycle {
  private overlay: Phaser.GameObjects.Rectangle;
  private elapsed = 0;

  constructor(scene: Phaser.Scene) {
    // Full-viewport rectangle, fixed to camera, on top of everything except HUD
    this.overlay = scene.add
      .rectangle(
        scene.scale.width / 2,
        scene.scale.height / 2,
        scene.scale.width * 2,
        scene.scale.height * 2,
      )
      .setScrollFactor(0)
      .setDepth(900000)
      .setBlendMode(Phaser.BlendModes.MULTIPLY)
      .setAlpha(0);
  }

  tick(deltaMs: number): void {
    this.elapsed += deltaMs;
    const t = (this.elapsed % CYCLE_DURATION_MS) / CYCLE_DURATION_MS;

    // Find the two surrounding keyframes
    let i = 0;
    while (i < PHASES.length - 1 && PHASES[i + 1]!.at <= t) i++;
    const from = PHASES[i]!;
    const to = PHASES[i + 1] ?? PHASES[0]!;
    const span = to.at - from.at || 1;
    const local = (t - from.at) / span;

    const tint = lerpColor(from.tint, to.tint, local);
    const alpha = from.alpha + (to.alpha - from.alpha) * local;

    this.overlay.setFillStyle(tint, 1);
    this.overlay.setAlpha(alpha);
  }

  destroy(): void {
    this.overlay.destroy();
  }
}
